import { CostChip, TokensChip } from '@/Pages/Run/components/TokensChip';
import { FeedbackChips } from '@/components/FeedbackChips';
import { RunLatencyChip } from '@/components/RunLatencyChip';
import { RunStatusChip } from '@/components/RunStatusChip';
import { FeedbackStatsField, RunSchema } from '@/types/schema';

import { COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW } from '../../constants';
import {
  AggregateFeedbackChips,
  AggregateMetricsChips,
} from '../AggregateFeedbackChips';

interface SingleOutputChipsProps {
  feedbackStats: FeedbackStatsField;
  run: RunSchema;
  mutateInfiniteExamples: () => void;
  datasetShareToken: string | undefined;
}

function SingleOutputChips({
  feedbackStats,
  run,
  mutateInfiniteExamples,
  datasetShareToken,
}: SingleOutputChipsProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-shrink flex-wrap items-start gap-2">
        <FeedbackChips
          feedbackStats={feedbackStats}
          allowTruncation
          feedbackSourceRunId={run.id}
          onMutate={mutateInfiniteExamples}
          disablePopover={!!datasetShareToken}
          maxNumberOfCategories={COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW}
        />
      </div>

      <div className="flex flex-shrink flex-wrap items-start gap-2">
        <RunLatencyChip run={run} />

        <RunStatusChip run={run} className="px-1.5" size="xs" />

        <TokensChip run={run} />
        <CostChip run={run} />
      </div>
    </div>
  );
}

interface AggregateOutputChipsProps {
  runs: RunSchema[];
  mutateInfiniteExamples: () => void;
  datasetShareToken: string | undefined;
}

function AggregateOutputChips({
  runs,
  mutateInfiniteExamples,
  datasetShareToken,
}: AggregateOutputChipsProps) {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2">
        <div className="flex flex-shrink flex-wrap items-start gap-2">
          <AggregateFeedbackChips
            runs={runs}
            onMutate={mutateInfiniteExamples}
            disablePopover={!!datasetShareToken}
            maxNumberOfCategories={COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW}
          />
        </div>

        <div className="flex flex-shrink flex-wrap items-start gap-2">
          <AggregateMetricsChips runs={runs} />
        </div>
      </div>
    </div>
  );
}

interface OutputChipsProps
  extends SingleOutputChipsProps,
    AggregateOutputChipsProps {}
export function OutputChips({
  runs,
  feedbackStats,
  run,
  mutateInfiniteExamples,
  datasetShareToken,
}: OutputChipsProps) {
  const showRepetitionsUI = runs.length > 1;
  if (showRepetitionsUI) {
    return (
      <AggregateOutputChips
        runs={runs}
        mutateInfiniteExamples={mutateInfiniteExamples}
        datasetShareToken={datasetShareToken}
      />
    );
  }
  return (
    <SingleOutputChips
      feedbackStats={feedbackStats}
      run={run}
      mutateInfiniteExamples={mutateInfiniteExamples}
      datasetShareToken={datasetShareToken}
    />
  );
}
